import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, HttpResponse } from '@wix/sdk-types';
import * as ambassadorWixEcomV1LocalDeliveryOption from './ecom-v1-local-delivery-option-local-delivery-options.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface LocalDeliveryOption extends LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  /**
   * LocalDeliveryOptionId - unique identifier of local delivery option
   * @readonly
   */
  _id?: string | null;
  /** Area name of Local Delivery Option */
  areaName?: string | null;
  /**
   * Flat price of local delivery
   * @deprecated Flat price of local delivery
   * @targetRemovalDate 2025-01-12
   */
  rate?: string | null;
  /** Estimated delivery time description text */
  estimatedDeliveryTime?: string | null;
  /**
   * Minimum order total to be eligible for free delivery
   * @deprecated Minimum order total to be eligible for free delivery
   * @targetRemovalDate 2025-01-12
   */
  minimumFreeDeliveryOrderAmount?: number | null;
  /** List of countries with subdivisions */
  destinations?: Destination[];
  /**
   * Database revision of LocalDeliveryOption entity. Incremented on each modification
   * @readonly
   */
  revision?: string | null;
  scheduledDeliveryConfig?: ScheduledDelivery;
  /** at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one. */
  rates?: ConditionalRates[];
  /**
   * Date and time the LocalDeliveryOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the LocalDeliveryOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** This local delivery is active for the following delivery regions. */
  deliveryRegionIds?: string[];
}

/** @oneof */
export interface LocalDeliveryOptionConfigOneOf {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
}

export interface Destination {
  /** Country ISO code */
  code?: string;
  /** List of subdivisions, empty means all */
  subdivisions?: string[];
}

export interface ZipCodeConfig {
  /** List of ZipCodes and ZipCode regexes. Examples: 10001, 10002 or 1000* */
  zipCodes?: string[];
}

export interface RadiusConfig {
  /** Delivery-from address */
  address?: LocalDeliveryAddress;
  /** Delivery circle's radius */
  radius?: number;
  /**
   * Radius' unit, taken from site properties
   * @readonly
   */
  lengthUnit?: LengthUnit;
}

export interface LocalDeliveryAddress {
  /** A string containing the human-readable address of this location */
  formatted?: string | null;
  /** coordinates of the physical address */
  location?: AddressLocation;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export enum LengthUnit {
  /** Length unit can't be classified, due to an error */
  UNSPECIFIED_LENGTH_UNIT = 'UNSPECIFIED_LENGTH_UNIT',
  /** Kilometers */
  KM = 'KM',
  /** Miles */
  MI = 'MI',
}

export interface CustomAreaConfig {
  /** Geocodes of the polygon defining the delivery area. */
  geocodes?: AddressLocation[];
}

export interface ScheduledDelivery {
  /**
   * Days config which contain days when delivery is enabled with it's time slots
   * @deprecated Days config which contain days when delivery is enabled with it's time slots
   * @replacedBy time_windows
   * @targetRemovalDate 2025-01-01
   */
  days?: Days;
  /** Amount of days before first delivery option date */
  handlingDays?: number | null;
  /** Cut off time after which first delivery option would be for next day */
  cutOffTime?: TimePoint;
  /** availability times configuration. */
  timeWindows?: TimeWindow[];
}

export interface Days {
  monday?: DaySlot;
  tuesday?: DaySlot;
  wednesday?: DaySlot;
  thursday?: DaySlot;
  friday?: DaySlot;
  saturday?: DaySlot;
  sunday?: DaySlot;
}

export interface DaySlot {
  /** Shows whether this day slot is enabled for delivery */
  enabledForDelivery?: boolean | null;
  /** Time slots for scheduled delivery during day */
  timeSlots?: TimeSlot[];
}

export interface TimeSlot {
  /** Starting time point of time slot */
  from?: TimePoint;
  /** End time point of time slot */
  to?: TimePoint;
}

export interface TimePoint {
  /** Hours in format 0-23 */
  hours?: number | null;
  /** Minutes 0-59 */
  minutes?: number | null;
}

export interface TimeWindow {
  /** The day of week this availability relates to. */
  day?: DayOfWeek;
  /** The start time in time of day representation. */
  start?: TimeOfDay;
  /** The end time in time of day representation. */
  end?: TimeOfDay;
  /** Whether this timeWindow availability is enabled. */
  enabled?: boolean | null;
}

export enum DayOfWeek {
  /** Monday. */
  MON = 'MON',
  /** Tuesday. */
  TUE = 'TUE',
  /** Wednesday. */
  WED = 'WED',
  /** Thursday. */
  THU = 'THU',
  /** Friday. */
  FRI = 'FRI',
  /** Saturday. */
  SAT = 'SAT',
  /** Sunday. */
  SUN = 'SUN',
}

export interface TimeOfDay {
  /** Hours */
  hours?: number;
  /** Minutes */
  minutes?: number;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * For example: weight > 0 and weight <= 10
   */
  conditions?: Condition[];
  /** The amount of the rate that will be returned if all conditions are met. */
  amount?: string;
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionType;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   */
  value?: string;
  operator?: LogicalOperator;
}

export enum ConditionType {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

export enum LogicalOperator {
  UNKNOWN_LOGICAL_OPERATOR_TYPE = 'UNKNOWN_LOGICAL_OPERATOR_TYPE',
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

export interface CreateLocalDeliveryOptionRequest {
  localDeliveryOption: LocalDeliveryOption;
}

export interface CreateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface GetLocalDeliveryOptionRequest {
  /**
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
  localDeliveryOptionId: string;
}

export interface GetLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface ListLocalDeliveryOptionsRequest {
  externalId: string;
}

export interface ListLocalDeliveryOptionsResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
}

export interface QueryLocalDeliveryOptionsRequest {
  /** WQL expression */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about the [filter section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-filter-section).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about the [sort section](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#the-sort-section).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrder;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /** Maximum number of items to return in the results. */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryLocalDeliveryOptionsResponse {
  /** Retrieved local delivery options. */
  localDeliveryOptions?: LocalDeliveryOption[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor string pointing to the next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to the previous page in the list of results. */
  prev?: string | null;
}

export interface UpdateLocalDeliveryOptionRequest {
  /** LocalDeliveryOption to be updated, may be partial. */
  localDeliveryOption: LocalDeliveryOption;
}

export interface UpdateLocalDeliveryOptionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface DeleteLocalDeliveryOptionRequest {
  /**
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
  localDeliveryOptionId: string;
}

export interface DeleteLocalDeliveryOptionResponse {}

export interface BulkCreateLocalDeliveryOptionRequest {
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkCreateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface LocalDeliveryOptionError {
  _id?: string;
  error?: string;
}

export interface BulkUpdateLocalDeliveryOptionRequest {
  localDeliveryOptions: LocalDeliveryOption[];
}

export interface BulkUpdateLocalDeliveryOptionResponse {
  localDeliveryOptions?: LocalDeliveryOption[];
  errors?: LocalDeliveryOptionError[];
}

export interface BulkDeleteLocalDeliveryOptionRequest {
  ids: string[];
}

export interface BulkDeleteLocalDeliveryOptionResponse {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /**
   * Unique event ID.
   * Allows clients to ignore duplicate webhooks.
   */
  _id?: string;
  /**
   * Assumes actions are also always typed to an entity_type
   * Example: wix.stores.catalog.product, wix.bookings.session, wix.payments.transaction
   */
  entityFqdn?: string;
  /**
   * This is top level to ease client code dispatching of messages (switch on entity_fqdn+slug)
   * This is although the created/updated/deleted notion is duplication of the oneof types
   * Example: created/updated/deleted/started/completed/email_opened
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example: 2020-04-26T13:57:50.699Z */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number defining the order of updates to the underlying entity.
   * For example, given that some entity was updated at 16:00 and than again at 16:01,
   * it is guaranteed that the sequence number of the second update is strictly higher than the first.
   * As the consumer, you can use this value to ensure that you handle messages in the correct order.
   * To do so, you will need to persist this number on your end, and compare the sequence number from the
   * message against the one you have stored. Given that the stored number is higher, you should ignore the message.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface AddDeliveryRegionRequest {
  deliveryRegionId: string;
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface AddDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface RemoveDeliveryRegionRequest {
  deliveryRegionId: string;
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

export interface RemoveDeliveryRegionResponse {
  localDeliveryOption?: LocalDeliveryOption;
}

export interface MessageEnvelope {
  /** App instance ID. */
  instanceId?: string | null;
  /** Event type. */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /** ID of a site visitor that has not logged in to the site. */
  anonymousVisitorId?: string;
  /** ID of a site visitor that has logged in to the site. */
  memberId?: string;
  /** ID of a Wix user (site owner, contributor, etc.). */
  wixUserId?: string;
  /** ID of an app. */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

interface ZipCodeConfigNonNullableFields {
  zipCodes: string[];
}

interface RadiusConfigNonNullableFields {
  radius: number;
  lengthUnit: LengthUnit;
}

interface DestinationNonNullableFields {
  code: string;
  subdivisions: string[];
}

interface TimeOfDayNonNullableFields {
  hours: number;
  minutes: number;
}

interface TimeWindowNonNullableFields {
  day: DayOfWeek;
  start?: TimeOfDayNonNullableFields;
  end?: TimeOfDayNonNullableFields;
}

interface ScheduledDeliveryNonNullableFields {
  timeWindows: TimeWindowNonNullableFields[];
}

interface ConditionNonNullableFields {
  type: ConditionType;
  value: string;
  operator: LogicalOperator;
}

interface ConditionalRatesNonNullableFields {
  conditions: ConditionNonNullableFields[];
  amount: string;
  multiplyByQuantity: boolean;
}

export interface LocalDeliveryOptionNonNullableFields {
  zipConfig?: ZipCodeConfigNonNullableFields;
  radiusConfig?: RadiusConfigNonNullableFields;
  destinations: DestinationNonNullableFields[];
  scheduledDeliveryConfig?: ScheduledDeliveryNonNullableFields;
  rates: ConditionalRatesNonNullableFields[];
  deliveryRegionIds: string[];
}

export interface CreateLocalDeliveryOptionResponseNonNullableFields {
  localDeliveryOption?: LocalDeliveryOptionNonNullableFields;
}

export interface GetLocalDeliveryOptionResponseNonNullableFields {
  localDeliveryOption?: LocalDeliveryOptionNonNullableFields;
}

export interface ListLocalDeliveryOptionsResponseNonNullableFields {
  localDeliveryOptions: LocalDeliveryOptionNonNullableFields[];
}

export interface QueryLocalDeliveryOptionsResponseNonNullableFields {
  localDeliveryOptions: LocalDeliveryOptionNonNullableFields[];
}

export interface UpdateLocalDeliveryOptionResponseNonNullableFields {
  localDeliveryOption?: LocalDeliveryOptionNonNullableFields;
}

interface LocalDeliveryOptionErrorNonNullableFields {
  _id: string;
  error: string;
}

export interface BulkCreateLocalDeliveryOptionResponseNonNullableFields {
  localDeliveryOptions: LocalDeliveryOptionNonNullableFields[];
  errors: LocalDeliveryOptionErrorNonNullableFields[];
}

export interface BulkUpdateLocalDeliveryOptionResponseNonNullableFields {
  localDeliveryOptions: LocalDeliveryOptionNonNullableFields[];
  errors: LocalDeliveryOptionErrorNonNullableFields[];
}

export interface AddDeliveryRegionResponseNonNullableFields {
  localDeliveryOption?: LocalDeliveryOptionNonNullableFields;
}

export interface RemoveDeliveryRegionResponseNonNullableFields {
  localDeliveryOption?: LocalDeliveryOptionNonNullableFields;
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOption
 * @requiredField localDeliveryOption.areaName
 * @requiredField localDeliveryOption.radiusConfig.address
 * @requiredField localDeliveryOption.radiusConfig.address.formatted
 * @requiredField localDeliveryOption.radiusConfig.address.location
 * @requiredField localDeliveryOption.rate
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_CREATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.CreateLocalDeliveryOption
 */
export async function createLocalDeliveryOption(
  localDeliveryOption: LocalDeliveryOption
): Promise<LocalDeliveryOption & LocalDeliveryOptionNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOption: localDeliveryOption,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.createLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOption: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.GetLocalDeliveryOption
 */
export async function getLocalDeliveryOption(
  localDeliveryOptionId: string,
  options?: GetLocalDeliveryOptionOptions
): Promise<LocalDeliveryOption & LocalDeliveryOptionNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    localDeliveryOptionId: localDeliveryOptionId,
    id: options?._id,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.getLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          localDeliveryOptionId: '$[0]',
          id: '$[1]._id',
        },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetLocalDeliveryOptionOptions {
  /**
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
}

/** @public
 * @documentationMaturity preview
 * @requiredField externalId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.ListLocalDeliveryOptions
 * @deprecated method is deprecated due to the fact that external_id is deprecated
 * @replacedBy ListLocalDeliveryOptionsByDeliveryRegion
 * @targetRemovalDate 2024-11-01
 */
export async function listLocalDeliveryOptions(
  externalId: string
): Promise<
  ListLocalDeliveryOptionsResponse &
    ListLocalDeliveryOptionsResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    externalId: externalId,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.listLocalDeliveryOptions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { externalId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['externalId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of local delivery options, given the provided paging, filtering, and sorting.
 *
 * For field support for filters and sorting, see [Local Delivery: Supported Filters and Sorting](https://dev.wix.com/docs/rest/business-management/payments/tax/tax-regions/supported-filters-and-sorting).
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_READ
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.QueryLocalDeliveryOptions
 */
export function queryLocalDeliveryOptions(): LocalDeliveryOptionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    LocalDeliveryOption,
    'CURSOR',
    QueryLocalDeliveryOptionsRequest,
    QueryLocalDeliveryOptionsResponse
  >({
    func: async (payload: QueryLocalDeliveryOptionsRequest) => {
      const reqOpts =
        ambassadorWixEcomV1LocalDeliveryOption.queryLocalDeliveryOptions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryLocalDeliveryOptionsRequest['query']) => {
      const args = [query, {}] as [
        QueryLocalDeliveryOptionsRequest['query'],
        {}
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryLocalDeliveryOptionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
          },
        ])
      );

      return {
        items: transformedData?.localDeliveryOptions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface LocalDeliveryOptionsQueryResult extends QueryCursorResult {
  items: LocalDeliveryOption[];
  query: LocalDeliveryOptionsQueryBuilder;
  next: () => Promise<LocalDeliveryOptionsQueryResult>;
  prev: () => Promise<LocalDeliveryOptionsQueryResult>;
}

export interface LocalDeliveryOptionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: '_id' | 'areaName' | 'estimatedDeliveryTime',
    value: string
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any[]
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: any
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_id'
      | 'areaName'
      | 'estimatedDeliveryTime'
      | 'deliveryRegionIds',
    value: boolean
  ) => LocalDeliveryOptionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => LocalDeliveryOptionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => LocalDeliveryOptionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<LocalDeliveryOptionsQueryResult>;
}

/** @param _id - LocalDeliveryOptionId - unique identifier of local delivery option
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField localDeliveryOption
 * @requiredField localDeliveryOption.radiusConfig.address
 * @requiredField localDeliveryOption.radiusConfig.address.formatted
 * @requiredField localDeliveryOption.radiusConfig.address.location
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOption.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.UpdateLocalDeliveryOption
 */
export async function updateLocalDeliveryOption(
  _id: string | null,
  localDeliveryOption: UpdateLocalDeliveryOption
): Promise<LocalDeliveryOption & LocalDeliveryOptionNonNullableFields> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOption: { ...localDeliveryOption, id: _id },
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.updateLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )?.localDeliveryOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { localDeliveryOption: '$[1]' },
        explicitPathsToArguments: { 'localDeliveryOption.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'localDeliveryOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateLocalDeliveryOption {
  zipConfig?: ZipCodeConfig;
  radiusConfig?: RadiusConfig;
  customAreaConfig?: CustomAreaConfig;
  /**
   * LocalDeliveryOptionId - unique identifier of local delivery option
   * @readonly
   */
  _id?: string | null;
  /** Area name of Local Delivery Option */
  areaName?: string | null;
  /**
   * Flat price of local delivery
   * @deprecated Flat price of local delivery
   * @targetRemovalDate 2025-01-12
   */
  rate?: string | null;
  /** Estimated delivery time description text */
  estimatedDeliveryTime?: string | null;
  /**
   * Minimum order total to be eligible for free delivery
   * @deprecated Minimum order total to be eligible for free delivery
   * @targetRemovalDate 2025-01-12
   */
  minimumFreeDeliveryOrderAmount?: number | null;
  /** List of countries with subdivisions */
  destinations?: Destination[];
  /**
   * Database revision of LocalDeliveryOption entity. Incremented on each modification
   * @readonly
   */
  revision?: string | null;
  scheduledDeliveryConfig?: ScheduledDelivery;
  /** at runtime for a given shipment input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one. */
  rates?: ConditionalRates[];
  /**
   * Date and time the LocalDeliveryOption was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the LocalDeliveryOption was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** This local delivery is active for the following delivery regions. */
  deliveryRegionIds?: string[];
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_DELETE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.DeleteLocalDeliveryOption
 */
export async function deleteLocalDeliveryOption(
  localDeliveryOptionId: string,
  options?: DeleteLocalDeliveryOptionOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    localDeliveryOptionId: localDeliveryOptionId,
    id: options?._id,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.deleteLocalDeliveryOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          localDeliveryOptionId: '$[0]',
          id: '$[1]._id',
        },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteLocalDeliveryOptionOptions {
  /**
   * @deprecated
   * @targetRemovalDate 2025-01-12
   */
  _id?: string;
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptions
 * @requiredField localDeliveryOptions.radiusConfig.address
 * @requiredField localDeliveryOptions.radiusConfig.address.formatted
 * @requiredField localDeliveryOptions.radiusConfig.address.location
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_CREATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkCreateLocalDeliveryOption
 */
export async function bulkCreateLocalDeliveryOption(
  localDeliveryOptions: LocalDeliveryOption[]
): Promise<
  BulkCreateLocalDeliveryOptionResponse &
    BulkCreateLocalDeliveryOptionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOptions: localDeliveryOptions,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkCreateLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField localDeliveryOptions
 * @requiredField localDeliveryOptions.radiusConfig.address
 * @requiredField localDeliveryOptions.radiusConfig.address.formatted
 * @requiredField localDeliveryOptions.radiusConfig.address.location
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.friday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.monday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.saturday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.sunday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.thursday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.tuesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.enabledForDelivery
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.from.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.hours
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.days.wednesday.timeSlots.to.minutes
 * @requiredField localDeliveryOptions.scheduledDeliveryConfig.handlingDays
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkUpdateLocalDeliveryOption
 */
export async function bulkUpdateLocalDeliveryOption(
  localDeliveryOptions: LocalDeliveryOption[]
): Promise<
  BulkUpdateLocalDeliveryOptionResponse &
    BulkUpdateLocalDeliveryOptionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      localDeliveryOptions: localDeliveryOptions,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkUpdateLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOptions.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { localDeliveryOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['localDeliveryOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_DELETE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.BulkDeleteLocalDeliveryOption
 */
export async function bulkDeleteLocalDeliveryOption(
  ids: string[]
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.bulkDeleteLocalDeliveryOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/** @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options.localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.AddDeliveryRegion
 */
export async function addDeliveryRegion(
  deliveryRegionId: string,
  options?: AddDeliveryRegionOptions
): Promise<
  AddDeliveryRegionResponse & AddDeliveryRegionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deliveryRegionId: deliveryRegionId,
    localDeliveryOptionId: options?.localDeliveryOptionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.addDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deliveryRegionId: '$[0]',
          localDeliveryOptionId: '$[1].localDeliveryOptionId',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDeliveryRegionOptions {
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}

/** @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options.localDeliveryOptionId
 * @permissionId ECOM.LOCAL_DELIVERY_OPTION_UPDATE
 * @fqn com.wix.ecom.shipments.localdelivery.LocalDelivery.RemoveDeliveryRegion
 */
export async function removeDeliveryRegion(
  deliveryRegionId: string,
  options?: RemoveDeliveryRegionOptions
): Promise<
  RemoveDeliveryRegionResponse & RemoveDeliveryRegionResponseNonNullableFields
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    deliveryRegionId: deliveryRegionId,
    localDeliveryOptionId: options?.localDeliveryOptionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1LocalDeliveryOption.removeDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'localDeliveryOption.radiusConfig.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          deliveryRegionId: '$[0]',
          localDeliveryOptionId: '$[1].localDeliveryOptionId',
          revision: '$[1].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveDeliveryRegionOptions {
  localDeliveryOptionId: string;
  /** Revision of the PickupLocation */
  revision?: string | null;
}
